import type { MatrixConfig, MatrixMessage, MatrixRoom } from './types';

type MatrixEvent = { event_id?: string; sender?: string; origin_server_ts?: number; type?: string; state_key?: string; content?: Record<string, unknown> };
type MatrixSync = {
  next_batch?: string;
  account_data?: { events?: MatrixEvent[] };
  rooms?: {
    join?: Record<string, { state?: { events?: MatrixEvent[] }; timeline?: { events?: MatrixEvent[] }; unread_notifications?: { notification_count?: number } }>;
    invite?: Record<string, unknown>;
  };
};

function homeserverURL(config: MatrixConfig, path: string): string {
  if (!config.homeserver) throw new Error('Matrix ist nicht eingerichtet.');
  return `${config.homeserver.replace(/\/$/, '')}${path}`;
}

export async function matrixRequest<T>(config: MatrixConfig, path: string, init: RequestInit = {}): Promise<T> {
  if (!config.access_token) throw new Error('Matrix ist nicht eingerichtet.');
  const response = await fetch(homeserverURL(config, path), {
    ...init,
    headers: { Authorization: `Bearer ${config.access_token}`, ...init.headers },
  });
  if (!response.ok) {
    let message = `Matrix HTTP ${response.status}`;
    try { const body = await response.json() as { error?: string }; if (body.error) message = body.error; } catch { /* use status */ }
    throw new Error(message);
  }
  return response.status === 204 ? undefined as T : await response.json() as T;
}

export async function matrixSync(config: MatrixConfig, since = ''): Promise<MatrixSync> {
  const query = new URLSearchParams({ timeout: since ? '15000' : '0' });
  if (since) query.set('since', since);
  return matrixRequest(config, `/_matrix/client/v3/sync?${query}`);
}

export async function joinMatrixRoom(config: MatrixConfig, roomID: string): Promise<void> {
  await matrixRequest(config, `/_matrix/client/v3/join/${encodeURIComponent(roomID)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
}

export async function sendMatrixText(config: MatrixConfig, roomID: string, body: string, transactionID: string): Promise<{ event_id: string }> {
  return matrixRequest(config, `/_matrix/client/v3/rooms/${encodeURIComponent(roomID)}/send/m.room.message/${encodeURIComponent(transactionID)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ msgtype: 'm.text', body }),
  });
}

export async function sendMatrixImage(config: MatrixConfig, roomID: string, filename: string, contentType: string, blob: Blob, transactionID: string): Promise<{ event_id: string; content_uri: string }> {
  const uploaded = await matrixRequest<{ content_uri: string }>(config, `/_matrix/media/v3/upload?filename=${encodeURIComponent(filename)}`, {
    method: 'POST', headers: { 'Content-Type': contentType || 'application/octet-stream' }, body: blob,
  });
  const sent = await matrixRequest<{ event_id: string }>(config, `/_matrix/client/v3/rooms/${encodeURIComponent(roomID)}/send/m.room.message/${encodeURIComponent(transactionID)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ msgtype: 'm.image', body: filename, url: uploaded.content_uri, info: { mimetype: contentType, size: blob.size } }),
  });
  return { ...sent, content_uri: uploaded.content_uri };
}

export async function createMatrixRoom(config: MatrixConfig, name: string, invitees: string[], direct: boolean): Promise<{ room_id: string }> {
  return matrixRequest(config, '/_matrix/client/v3/createRoom', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ preset: 'private_chat', name, invite: invitees, is_direct: direct }),
  });
}

export function matrixMediaPath(mxcURI: string): string {
  if (!mxcURI.startsWith('mxc://')) throw new Error('Ungültige Matrix-Bildadresse.');
  const [server, ...mediaParts] = mxcURI.slice(6).split('/');
  const mediaID = mediaParts.join('/');
  if (!server || !mediaID) throw new Error('Ungültige Matrix-Bildadresse.');
  return `/_matrix/client/v1/media/download/${encodeURIComponent(server)}/${mediaID.split('/').map(encodeURIComponent).join('/')}`;
}

export async function downloadMatrixMedia(config: MatrixConfig, mxcURI: string): Promise<Blob> {
  if (!config.access_token) throw new Error('Matrix ist nicht eingerichtet.');
  const response = await fetch(homeserverURL(config, matrixMediaPath(mxcURI)), { headers: { Authorization: `Bearer ${config.access_token}` } });
  if (!response.ok) throw new Error(`Bild konnte nicht geladen werden (HTTP ${response.status}).`);
  const blob = await response.blob();
  if (blob.size > 25 * 1024 * 1024) throw new Error('Das Originalbild ist größer als 25 MB.');
  return blob;
}

function messageFromEvent(roomID: string, event: MatrixEvent): MatrixMessage | null {
  if (!event.event_id || !event.sender) return null;
  if (event.type === 'm.room.encrypted') return { event_id: event.event_id, room_id: roomID, sender: event.sender, timestamp: Number(event.origin_server_ts || Date.now()), msgtype: 'unsupported', body: 'Verschlüsselte Nachricht – bitte in einem Matrix-Client öffnen.' };
  if (event.type !== 'm.room.message') return null;
  const content = event.content || {};
  const rawType = String(content.msgtype || 'unsupported');
  const msgtype: MatrixMessage['msgtype'] = ['m.text', 'm.notice', 'm.image', 'm.file'].includes(rawType) ? rawType as MatrixMessage['msgtype'] : 'unsupported';
  return {
    event_id: event.event_id,
    room_id: roomID,
    sender: event.sender,
    timestamp: Number(event.origin_server_ts || Date.now()),
    msgtype,
    body: String(content.body || (msgtype === 'unsupported' ? 'Nicht unterstützte Nachricht' : '')),
    mxc_uri: typeof content.url === 'string' ? content.url : undefined,
    content_type: typeof (content.info as Record<string, unknown> | undefined)?.mimetype === 'string' ? String((content.info as Record<string, unknown>).mimetype) : undefined,
    size: Number((content.info as Record<string, unknown> | undefined)?.size || 0) || undefined,
  };
}

function roomName(state: MatrixEvent[], ownUser = ''): string {
  const explicit = state.find(event => event.type === 'm.room.name' && typeof event.content?.name === 'string')?.content?.name;
  if (explicit) return String(explicit);
  const alias = state.find(event => event.type === 'm.room.canonical_alias' && typeof event.content?.alias === 'string')?.content?.alias;
  if (alias) return String(alias);
  const other = state.find(event => event.type === 'm.room.member' && event.state_key && event.state_key !== ownUser && event.content?.membership === 'join');
  if (typeof other?.content?.displayname === 'string') return String(other.content.displayname);
  return '';
}

export function mergeMatrixSync(existing: MatrixRoom[], sync: MatrixSync, ownUser = ''): MatrixRoom[] {
  const directIDs = new Set<string>();
  for (const event of sync.account_data?.events || []) {
    if (event.type !== 'm.direct' || !event.content) continue;
    for (const roomIDs of Object.values(event.content)) if (Array.isArray(roomIDs)) for (const id of roomIDs) if (typeof id === 'string') directIDs.add(id);
  }
  const rooms = new Map(existing.map(room => [room.room_id, room]));
  for (const [roomID, data] of Object.entries(sync.rooms?.join || {})) {
    const previous = rooms.get(roomID);
    const state = data.state?.events || [];
    const incoming = (data.timeline?.events || []).map(event => messageFromEvent(roomID, event)).filter((value): value is MatrixMessage => Boolean(value));
    const messages = new Map((previous?.messages || []).map(message => [message.event_id, message]));
    for (const message of incoming) messages.set(message.event_id, { ...messages.get(message.event_id), ...message });
    rooms.set(roomID, {
      room_id: roomID,
      name: roomName(state, ownUser) || previous?.name || roomID,
      direct: directIDs.has(roomID) || previous?.direct || false,
      unread_count: Number(data.unread_notifications?.notification_count || 0),
      messages: [...messages.values()].sort((a, b) => a.timestamp - b.timestamp).slice(-120),
    });
  }
  return [...rooms.values()].sort((a, b) => (b.messages.at(-1)?.timestamp || 0) - (a.messages.at(-1)?.timestamp || 0));
}

export function invitedRoomIDs(sync: MatrixSync): string[] { return Object.keys(sync.rooms?.invite || {}); }
